/**
 * Линкификация упоминаний сущностей `Тип."Имя"` в тексте и markdown.
 *
 * Агент и люди естественно пишут `Сегмент."Кофейни 1-2 точки"` — тот же
 * формат, что путь-ссылки Сигналов и target комментариев. Превращаем такие
 * упоминания в кликабельные ссылки, открывающие карточку сущности
 * (ui-store.openCard) — агент и комментарии становятся навигаторами по модели.
 *
 * Для markdown: препроцессинг строки — упоминание заменяется на
 * `[Тип."Имя"](#entity=...)`, а рендерер `a` перехватывает `#entity=`.
 * Уже-ссылки и код-блоки не трогаем (грубая защита: не заменяем внутри
 * `[...]`(...)` и в back-tick'ах через сплит по сегментам кода).
 */

import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { useUiStore } from '../stores/ui-store';

/** `Тип."Имя"` — тип с большой русской буквы (как кейворды DSL). */
const MENTION_RE = /([А-ЯЁ][а-яё_]+)\."([^"\n]{1,120})"/g;

export function entityHref(type: string, name: string): string {
    return `#entity=${encodeURIComponent(type)}:${encodeURIComponent(name)}`;
}

export function parseEntityHref(href: string): { type: string; name: string } | null {
    if (!href.startsWith('#entity=')) return null;
    const raw = href.slice('#entity='.length);
    const sep = raw.indexOf(':');
    if (sep < 0) return null;
    try {
        return {
            type: decodeURIComponent(raw.slice(0, sep)),
            name: decodeURIComponent(raw.slice(sep + 1)),
        };
    } catch {
        return null;
    }
}

/**
 * Препроцессинг markdown: `Тип."Имя"` → `[Тип."Имя"](#entity=...)`.
 * Код-сегменты (```...``` и `...`) не трогаются.
 */
export function linkifyMentionsInMarkdown(md: string): string {
    // Сплит по код-сегментам: нечётные индексы — код, их не трогаем.
    const parts = md.split(/(```[\s\S]*?```|`[^`\n]*`)/g);
    return parts
        .map((part, i) => {
            if (i % 2 === 1) return part;
            return part.replace(MENTION_RE, (m, type: string, name: string, offset: number, s: string) => {
                // Не линкуем, если это уже текст markdown-ссылки: перед
                // совпадением стоит '[' или сразу после — '](' .
                const before = s.slice(Math.max(0, offset - 1), offset);
                const after  = s.slice(offset + m.length, offset + m.length + 2);
                if (before === '[' || after.startsWith('](')) return m;
                return `[${m}](${entityHref(type, name)})`;
            });
        })
        .join('');
}

/**
 * Компонент-перехватчик для ReactMarkdown `components={{ a: MentionAwareLink }}`:
 * `#entity=`-ссылки открывают карточку, остальные — обычные внешние ссылки.
 */
export function MentionAwareLink(
    props: AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode },
): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    const { href, children, ...rest } = props;
    const ref = href ? parseEntityHref(href) : null;
    if (ref) {
        return (
            <a
                {...rest}
                href={href}
                style={{ cursor: 'pointer' }}
                onClick={e => {
                    e.preventDefault();
                    openCard({ type: ref.type, name: ref.name });
                }}
            >
                {children}
            </a>
        );
    }
    return <a {...rest} href={href} target="_blank" rel="noreferrer">{children}</a>;
}
